import { useEffect, useRef, useState } from "react";

import { fetchVastAd, firePixels, isHttpUrl, VastAd } from "./vast";

type VastVideoHandlers = {
  onPlay: () => void;
  onClick: () => void;
  onError: () => void;
};

/**
 * Loads a VAST tag and wires its tracking semantics: the impression fires once
 * on first play, clicks fire click-tracking and open the click-through, and
 * media failures fire the error pixels before surfacing to `onError`.
 */
export function useVastAd(
  src: string,
  onError: (error: Error) => void
): { ad: VastAd; handlers: VastVideoHandlers } | null {
  const [ad, setAd] = useState<VastAd | null>(null);
  const impressionFired = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    impressionFired.current = false;

    fetchVastAd(src, controller.signal)
      .then((parsed) => {
        if (controller.signal.aborted) {
          return;
        }
        if (!parsed) {
          onError(new Error(`Failed to parse VAST ad for the url: ${src}`));
          return;
        }
        setAd(parsed);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        onError(
          error instanceof Error
            ? error
            : new Error(`Failed to load VAST ad for the url: ${src}`)
        );
      });

    return () => {
      controller.abort();
    };
  }, [src, onError]);

  if (!ad) {
    return null;
  }

  const handlers: VastVideoHandlers = {
    onPlay: () => {
      if (impressionFired.current) {
        return;
      }
      impressionFired.current = true;
      firePixels(ad.impressions);
    },
    onClick: () => {
      firePixels(ad.clickTrackings);
      if (ad.clickThrough && isHttpUrl(ad.clickThrough)) {
        window.open(ad.clickThrough, "_blank", "noopener,noreferrer");
      }
    },
    onError: () => {
      firePixels(ad.errors);
      onError(new Error(`Failed to play VAST ad media: ${ad.mediaUrl}`));
    },
  };

  return { ad, handlers };
}
